import React, { useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import { IconButton, Link, Tooltip } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import { CompanyDetail } from '../../interfaces/companyDetail.interface';
import { USER_TYPES } from '../../services/user';
import { COMPANY_TYPES } from '../../services/vendor';
import DownloadReportSelect from '../DownloadReport/DownloadReportSelect';

const LEGACY_ASSESSMENTS_URL = 'https://mpaa.app.box.com/folder/188662031553';

const PaperItem = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#F8F9FD',
  padding: '16px',
  boxShadow: 'none',
  border: 'none',
  height: '100%',
}));

// White elevation-1 card, matching the prototype header (background #fff, radius 4,
// MUI elevation-1 shadow).
const HeaderCard = styled('div')({
  background: '#fff',
  borderRadius: 4,
  boxShadow: '0 1px 3px rgba(0,0,0,0.12),0 1px 2px rgba(0,0,0,0.06)',
  padding: 24,
  position: 'relative',
  marginBottom: 24,
});

const HeaderGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1.15fr 1fr 1fr',
  gap: 24,
  alignItems: 'start',
  '@media (max-width:900px)': { gridTemplateColumns: '1fr' },
});

const IdentityColumn = styled('div')({ display: 'flex', gap: 18, alignItems: 'flex-start' });

// 118px tile on the v2 navigation navy. Shows the company logo (contained, so its
// aspect ratio is preserved and never cropped) when one loads; the initials show
// when there is no logo or it fails to load (see LogoImage) — never a broken-image icon.
const AvatarTile = styled('div')({
  width: 118,
  height: 118,
  borderRadius: 10,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#142F53',
  color: '#fff',
  fontWeight: 700,
  fontSize: 34,
  overflow: 'hidden',
  padding: 12,
});

function companyInitials(name?: string): string {
  return (name || '').split(' ').filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
}

// Renders the logo contained within the tile; on a load failure it swaps to the
// fallback (initials) so a broken file never surfaces a broken-image icon. Keyed by
// src at the call site, so the failed state resets when the logo changes.
function LogoImage({ src, alt, fallback }: { src: string; alt: string; fallback: React.ReactNode }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <>{fallback}</>;
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
    />
  );
}

function getQualifications(detail: CompanyDetail): string {
  const qualifications: string[] = [];
  if (detail.canAssessPrem) qualifications.push('Site');
  if (detail.canAssessCloud) qualifications.push('Cloud');
  if (detail.assessorProfile?.hasZeroTrustCertification) qualifications.push('Zero Trust');
  return qualifications.join(', ');
}

export interface CompanyProfileHeaderProps {
  userType?: string;
  userCanAdminCompany: boolean;
  actionsMenuOpen: boolean;
  detail: CompanyDetail | null;
  loading: boolean;
  onAddLogoClick: () => void;
  onActionsMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
}

export default function CompanyProfileHeader(props: CompanyProfileHeaderProps) {
  const {
    userType,
    userCanAdminCompany,
    actionsMenuOpen,
    detail,
    loading,
    onAddLogoClick,
    onActionsMenuClick,
  } = props;

  const canClickSubsidiariesLink = useMemo(
    () => userType === USER_TYPES.TPN_ADMIN || userType === USER_TYPES.CONTENT_OWNER,
    [userType],
  );

  const qualifications = useMemo(() => (detail ? getQualifications(detail) : ''), [detail]);

  if (loading || !detail) {
    return (
      <HeaderCard>
        <HeaderGrid>
          {[0, 1, 2].map((column) => (
            <Box key={column}>
              <Skeleton variant="rectangular" height={28} sx={{ mb: 1, borderRadius: 1 }} />
              <Skeleton variant="text" />
              <Skeleton variant="text" />
              <Skeleton variant="text" width="60%" />
            </Box>
          ))}
        </HeaderGrid>
      </HeaderCard>
    );
  }

  return (
    <HeaderCard>
      {userCanAdminCompany && (
        <Tooltip title="Company Actions">
          <IconButton
            onClick={onActionsMenuClick}
            size="small"
            sx={{ position: 'absolute', top: 12, right: 12 }}
            aria-controls={actionsMenuOpen ? 'company-actions-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={actionsMenuOpen ? 'true' : undefined}
          >
            <MoreHorizIcon />
          </IconButton>
        </Tooltip>
      )}
      <HeaderGrid>
        {/* Identity: avatar + company name */}
        <IdentityColumn>
          <AvatarTile>
            {detail.logo
              ? <LogoImage key={detail.logo} src={detail.logo} alt={`${detail.name ?? 'Company'} logo`} fallback={companyInitials(detail.name)} />
              : companyInitials(detail.name)}
          </AvatarTile>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 400, color: '#1c2a42', lineHeight: 1.2 }}>{detail.name}</Typography>
            {(!detail.logo && userCanAdminCompany) && (
              <Button size='small' variant='outlined' onClick={onAddLogoClick} sx={{ mt: 1 }}>add logo</Button>
            )}
            {(!!detail.parentCompany && userType === USER_TYPES.TPN_ADMIN) && (
              <Typography
                variant="body1"
                color="text.secondary"
                component="p"
                sx={{ mt: 0.5, display: 'inline-flex', alignItems: 'baseline', gap: '0.35ch' }}
              >
                <span>A</span>
                {canClickSubsidiariesLink ? (
                  <Link
                    href={`/registry/${detail.parentCompany.id}`}
                    underline="none"
                    color="inherit"
                    sx={{
                      fontWeight: 700,
                      maxWidth: '100%',
                      display: 'inline-block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      '&:focus-visible': { outline: '2px solid', outlineOffset: 2 },
                    }}
                    title={detail.parentCompany.name}
                  >
                    {detail.parentCompany.name}
                  </Link>
                ) : (
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 700,
                      maxWidth: '100%',
                      display: 'inline-block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      cursor: 'default',
                    }}
                    title={detail.parentCompany.name}
                  >
                    {detail.parentCompany.name}
                  </Box>
                )}
                <span>company</span>
              </Typography>
            )}
            <Typography variant="body2" sx={{ color: '#8a94a3', fontSize: 12, mt: 1.25 }}>
              {detail.akaNames && detail.akaNames.length > 0 && <span>AKA: {detail.akaNames.join(', ')}</span>}
              {detail.fkaNames && detail.fkaNames.length > 0 && <span><br/>FKA: {detail.fkaNames.join(', ')}</span>}
              {detail.dbaNames && detail.dbaNames.length > 0 && <span><br/>DBA: {detail.dbaNames.join(', ')}</span>}
            </Typography>
            {detail.type === COMPANY_TYPES.CONTENT_OWNER && <Box sx={{ mt: 1 }}><Link href={LEGACY_ASSESSMENTS_URL} target='_blank'>View Legacy Assessments</Link></Box>}
            {detail.type === COMPANY_TYPES.CONTENT_OWNER && [USER_TYPES.CONTENT_OWNER, USER_TYPES.TPN_ADMIN].includes(userType || '') && <DownloadReportSelect />}
            {detail.type === COMPANY_TYPES.ASSESSOR && (
              <Box sx={{ display: 'flex', gap: 2, mt: 1, pt: 1, borderTop: '1px solid rgba(0,0,0,.2)' }}>
                <Typography>Level: {detail.assessorLevel?.descriptor}</Typography>
                {qualifications && <Typography>Qualifications: {qualifications}</Typography>}
              </Box>
            )}
          </Box>
        </IdentityColumn>

        {/* Address */}
        <PaperItem>
          <Typography variant="subtitle2">Address:</Typography>
          {detail.contact && (
            <Stack direction='column' spacing={2}>
              <Typography>
                {detail.contact.address}<br/>
                {detail.contact?.address2 && <>{detail.contact.address2}<br/></>}
                {detail.contact?.address3 && <>{detail.contact.address3}<br/></>}
                {(() => {
                  const cityState = [detail.contact.city, detail.contact.state].filter(Boolean).join(', ');
                  const cityStateZip = [cityState, detail.contact.zipcode].filter(Boolean).join(' ');
                  return cityStateZip ? <>{cityStateZip}<br/></> : null;
                })()}
                {detail.contact.country}<br/><br />
                {detail.contact.phoneNumber}<br />
                {detail.domain && (
                  <Link
                    href={detail.domain.startsWith('http') ? detail.domain : `https://${detail.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  >
                    {detail.domain}
                  </Link>
                )}
              </Typography>
            </Stack>
          )}
        </PaperItem>

        {/* Primary Contact */}
        <Box>
          {detail.primaryContactUser && (
            <>
              <Typography variant="subtitle2">Primary Contact:</Typography>
              <Stack direction='column' spacing={2}>
                <Typography sx={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
                  {detail.primaryContactUser?.firstName} {detail.primaryContactUser?.lastName}<br/>
                  <Tooltip title={detail.primaryContactUser?.email}>
                    <Link href={`mailto:${detail.primaryContactUser?.email}`} sx={{ textOverflow: 'ellipsis', margin: 0, overflow: 'hidden', cursor: 'pointer' }}>{detail.primaryContactUser?.email}</Link>
                  </Tooltip><br />
                  {detail.primaryContactUser?.contact?.phoneNumber}
                </Typography>
              </Stack>
            </>
          )}
        </Box>
      </HeaderGrid>
    </HeaderCard>
  );
}
